<template>
  <div class="Editor-container">
    <div class="Editor-header">
      <div class="Editor-title">{{ title }}{{ $t('编辑器') }}</div>
      <div class="Editor-view-mode-group-container">
        <div class="Editor-view-mode-group">
          <div :class="'Editor-view-mode-btn '+(!isMobile ? 'is-active' : '')" @click="isMobile = false">
            {{ $t('电脑') }}
          </div>
          <div :class="'Editor-view-mode-btn '+(isMobile ? 'is-active' : '')" @click="isMobile = true">
            {{ $t('手机') }}
          </div>
        </div>
      </div>

      <div class="Editor-header-actions">
        <div :class="'header-action-btn margin-left-space '" @click="togglePreview">
          {{isPreview ? $t('编辑') : $t('预览')}}
        </div>
        <div v-if="!isPreview" :class="'header-action-btn'" @click="saveDesign">
          {{ $t('保存') }}
        </div>
        <div v-if="!isPreview" :class="'header-action-btn'" @click="handleRelease">
          {{ $t('发布') }}
        </div>
      </div>
    </div>
    <FormEditor v-model="schema" :plugins="plugins" :isMobile="isMobile" :locale="locale" :mode="mode" :appId="mixinAppId" :pageId="mixinPageId" class="form-editor" />
  </div>
</template>

<script>
import { ShortcutKey } from 'amis-editor'
import { ReactInVue } from "vuera"
import FormEditor from '@/components/FormEditor'
import { getLatestDesign, saveDesign, changePageStatus } from '@/api/app/page'
import RouteQueryMixins from '@/mixins/RouteQueryMixins'
import LanguageSwitchMixin from './mixins/LanguageSwitchMixin'

export default {
  components: {
    FormEditor,
    AmisShortcutKey: ReactInVue(ShortcutKey)
  },
  data() {
    return {
      pageData: {},
      title: '',
      isMobile: false,
      plugins: [],
      mode: 'design',
      schema: {},
      pageKey: null,
      designData: null,
    }
  },
  mixins: [RouteQueryMixins, LanguageSwitchMixin],
  computed: {
    isPreview() {
      return this.mode === 'review'
    },
    locale() {
      return this.$store.getters.lang.lang
    }
  },
  mounted() {
    this.loadData()
  },
  watch: {
    '$route': {
      handler(newVal) {
        const route = newVal
        if (!(route.matched?.length)) {
          return
        }
        const component = route.matched[route.matched.length - 1]
        const vueComponent = component.instances?.default
        if (vueComponent != this) {
          return
        }
        const { pageKey } = newVal.query
        if (this.pageKey == pageKey) {
          return
        }
        this.resetData()
      },
      immediate: true,
      deep: true
    },
    'mode': {
      handler(val) {        
        if (val !== 'design') {
          const obj = this.schema
          this.designData = JSON.parse(JSON.stringify(obj))
          this.resetLabelByLanguage(obj, this.getLanguageMap(obj))
          this.schema = obj
          return
        }
        this.schema = this.designData
      }
    },
  },
  methods: {
    resetData() {
      this.pageKey = this.$route.query.pageKey
      this.pageData = {}
      this.title = ''
      this.isMobile = false
      this.mode = 'design'
      this.schema = {}
      this.designData = {}
      this.loadData()
    },
    loadData() {
      getLatestDesign(this.mixinPageId).then(res => {
        this.pageData = { ...res.data }
        const obj = this.fetchDesign(this.pageData) || {}
        this.fillGlobalDataIfNeed(obj)
        this.schema = obj
        this.designData = obj
        this.title = this.pageData.pageName
      })
    },
    fetchDesign(data) {
      const design = data.design
      const obj = design && design.length > 0 ? JSON.parse(design) : { 'type': 'page', 'body': [] }
      return obj
    },
    fillGlobalDataIfNeed(design) {
      if (!design) {
        return
      }
      const _PAGE_ORIGIN_PATH_ = window.location.origin
      const _PAGE_FULL_PATH_ = window.location.href
      const _PAGE_PATH_NAME_ = window.location.pathname
      design.data = {
        ...design.data,
        _PAGE_ORIGIN_PATH_,
        _PAGE_FULL_PATH_,
        _PAGE_PATH_NAME_
      }
    },
    togglePreview() {
      this.mode = this.mode === 'design' ? 'review' : 'design'
    },
    handleLocaleChange(e) {
      const locale = e.target.value
      this.locale = locale
    },
    handleRelease() {
      this.$confirm({
        title: this.$t('确认发布吗？'),
        onOk: () => {
          changePageStatus({ id: this.pageData.id, status: '2', design: JSON.stringify(this.schema) }).then(res => {
            if (res.code === 0) {
              this.$message.success(this.$t('发布成功'))
            } else {
              this.$message.error(res.message || this.$t('发布失败'))
            }
          })
        }
      })
    },
    saveDesign() {
      const commitData = {
        pageId: this.mixinPageId,
        design: JSON.stringify(this.schema)
      }
      saveDesign(commitData).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('保存成功'))
          this.loadData()
        } else {
          this.$message.error(res.message || this.$t('保存失败'))
        }
      })
    }
  }
}
</script>
<style lang="less" scoped>
@import "~@/components/theme.less";

@editor-default-color: #151b26; // 默认的字体色
@editor-active-color: @primary-color;
@editor-hover-color: @primary-color-active;
@editor-border-color: #e8e9eb; // 默认的边框色
@default-icon-color: #84868c; // 默认的icon颜色

@active-bg-color: #e6f0ff; // 激活态的背景色
@hover-bg-color: #f7f7f9; // 激活态的背景色

@disabled-color: #b8babf; // 禁用文字颜色
@disabled-bg-color: #f7f7f9; // 禁用背景颜色

@dark: #000000;

.Editor-container {
  position: relative;
  height: 100vh;
  min-height: 510px;
  display: flex;
  flex-direction: column;

  .Editor-header {
    flex: 0 0 48px;
    position: relative;
    background: #fff;
    // box-shadow: 0 2px 7px 0 rgba(232,232,232,0.50);
    display: flex;
    border-bottom: 1px solid @editor-border-color;
    box-sizing: border-box;
    z-index: 1000;

    svg {
      display: inline-block;
      width: 16px;
    }

    .Editor-title {
      flex: 1 1 565px;
      padding: 0 15px;
      font-family: PingFangSC-Medium;
      font-size: 16px;
      color: #151a26;
      font-weight: 500;
      letter-spacing: 0;
      user-select: none;
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }

    .Editor-view-mode-group-container {
      flex: 0 1 150px;
      display: flex;
      justify-content: center;
      align-items: center;
  
      .Editor-view-mode-group {
        font-size: 14px;
        font-family: PingFangSC-Regular;
        // color: #fff;
        letter-spacing: 0;
        text-align: center;
        height: 32px;
        border-radius: 4px;
        font-weight: 400;
        background-color: #f2f2f4;
  
        display: flex;
        justify-content: center;
        align-items: center;
  
        .Editor-view-mode-btn {
          user-select: none;
          padding: 0 15px;
          border-radius: 4px;
          height: 24px;
          cursor: pointer;
          transition: transform ease-out 0.2s;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          color: #666;

          &:hover {
            background-color: #f2f2f4;
          }
  
          svg {
            color: @editor-default-color;
          }
  
          &:first-child {
            margin-right: 12px;
          }
  
          &:hover > svg {
            color: @editor-active-color;
          }
  
          &.is-active {
            background: @editor-active-color;
            color: #fff;
  
            svg {
              color: #fff;
            }
  
            &:hover {
              background: #5086f5;
            }
          }
        }
      }
    }
  
    .Editor-header-actions {
      flex: 1 1 565px;
      padding: 0 24px;
      font-size: 12px;
      white-space: nowrap;
      display: flex;
      justify-content: flex-end;
      align-items: center;
  
      .header-action-item {
        margin-left: 18px;
        user-select: none;
        cursor: pointer;
        transition: transform ease-out 0.2s;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
  
        > svg {
          width: 16px;
          height: 16px;
          fill: @editor-default-color;
        }
  
        &:hover > svg {
          fill: @editor-active-color;
        }
  
        &.disabled {
          cursor: not-allowed;
  
          > svg {
            fill: @disabled-color;
          }
        }
  
        // 历史记录icon单独处理
        > svg.icon-editor-history {
          color: @editor-default-color;
  
          &:hover {
            color: @editor-active-color;
          }
  
          &.disabled,
          &.disabled:hover {
            cursor: not-allowed;
            color: @disabled-color;
          }
        }
      }
  
      .header-action-btn {
        margin-left: 8px;
        user-select: none;
        cursor: pointer;
        padding: 0 16px;
        min-width: 72px;
        height: 32px;
        font-family: PingFangSC-Regular;
        font-size: 12px;
        line-height: 20px;
        color: #fff;
        font-weight: 400;
        background: @editor-active-color;
        border-color: 1px solid @editor-active-color;
        border-radius: 4px;
        transition: transform ease-out 0.2s;
        display: inline-flex;
        justify-content: center;
        align-items: center;
  
        &.preview-btn {
          color: #151a26;
          border: 1px solid #dadbdd;
          background: #fff;
          border-radius: 4px;
  
          &:hover {
            color: @editor-active-color;
            border-color: @editor-active-color;
            background: #fff;
          }
        }
  
        &:hover {
          color: #fff;
          background: @editor-hover-color;
          border-color: @editor-hover-color;
        }
  
        &.disabled {
          cursor: not-allowed;
          color: @disabled-color;
          background-color: @disabled-bg-color;
          border-color: @disabled-bg-color;
        }
      }
  
      .margin-left-space {
        margin-left: 136px;
      }

      ::v-deep .shortcut-icon-btn {
        width: 20px;
        height: 20px;
      }
    }
  }
  .Editor-inner {
    position: relative;
    flex: 1 1 auto;
    overflow-x: hidden;
    overflow-y: auto;
  }
}

.resizer {
  position: absolute;
  top: 0;
  bottom: 0;
  margin-left: -2px;
  width: 4px;
  background: lighten(@dark, 20%);
  z-index: 1;
  cursor: col-resize;

  &:hover {
    background: @dark;
  }
}

// 修正被影响的样式
.editor-left-panel .editorPanel-tabs .editor-tab-icon>svg {
  width: 42px !important;
  height: 42px !important;
}
.ae-Breadcrumb .ae-Breadcrumb-content>ul>li>span {
  line-height: 42px !important;
}
.ae-BoxModel-inner {
  height: auto !important;
}

.width100 {
  width: 100%;
}

.form-editor {
  height: 100vh;

  ::v-deep .cxd-Table-fixedTop::after {
    height: 100%;
    box-shadow: none;
  }
}

</style>
